/* ==========================================================================
   Softline theme — scripts
   Vanilla JS, custom elements. No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------- Helpers ---------- */
  function debounce(fn, wait = 300) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function formatMoney(cents, format) {
    format = format || theme.moneyFormat || '${{amount}}';
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    };
    const match = format.match(placeholder);
    if (!match) return format;
    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  }
  theme.formatMoney = formatMoney;

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function showToast(message) {
    const toast = qs('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(() => (toast.hidden = true), 2600);
  }

  /* ---------- Scroll lock & focus trap ---------- */
  let lockCount = 0;
  function lockScroll() {
    lockCount += 1;
    document.body.classList.add('is-locked');
  }
  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.classList.remove('is-locked');
  }

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container) {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  /* ==========================================================================
     Drawer (cart, menu, search, filters)
     ========================================================================== */
  class SideDrawer extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) {
          e.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      // close any other open drawer first
      qsa('side-drawer.is-open, cart-drawer.is-open').forEach((d) => d !== this && d.close(false));
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
      lockScroll();
      this._releaseTrap = trapFocus(this);
      const focusTarget = qs('[data-autofocus]', this) || qs('.drawer__panel', this);
      setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 80);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'false');
      unlockScroll();
      if (this._releaseTrap) this._releaseTrap();
      if (restoreFocus && this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }
  customElements.define('side-drawer', SideDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (drawer && typeof drawer.open === 'function') {
      e.preventDefault();
      drawer.open(opener);
    }
  });

  /* ==========================================================================
     Cart
     ========================================================================== */
  const Cart = {
    sectionIds() {
      return [...new Set(qsa('cart-items').map((el) => el.dataset.sectionId).filter(Boolean))];
    },

    renderSections(sections) {
      if (!sections) return;
      qsa('cart-items').forEach((el) => {
        const html = sections[el.dataset.sectionId];
        if (!html) return;
        const doc = parseHTML(html);
        const fresh = doc.getElementById(el.id);
        if (fresh) {
          el.innerHTML = fresh.innerHTML;
          if (fresh.dataset.cartCount !== undefined) el.dataset.cartCount = fresh.dataset.cartCount;
        }
      });
      const counter = qs('cart-items[data-cart-count]');
      if (counter) this.updateCount(parseInt(counter.dataset.cartCount, 10) || 0);
    },

    updateCount(count) {
      qsa('[data-cart-count-bubble]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0 && el.hasAttribute('data-hide-empty');
      });
    },

    async refresh() {
      const ids = this.sectionIds();
      if (!ids.length) return;
      const res = await fetch(`${theme.routes.cart}?sections=${ids.join(',')}`);
      const sections = await res.json();
      this.renderSections(sections);
    },

    async change(key, quantity) {
      const res = await fetch(`${theme.routes.cartChange}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: this.sectionIds(),
          sections_url: window.location.pathname
        })
      });
      const data = await res.json();
      if (data.status && data.status !== 200) throw new Error(data.description || data.message);
      this.renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },

    async updateNote(note) {
      await fetch(`${theme.routes.cartUpdate}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note })
      });
    },

    openDrawer(opener) {
      const drawer = document.getElementById('CartDrawer');
      if (drawer) drawer.open(opener);
    }
  };
  theme.cart = Cart;

  class CartDrawer extends SideDrawer {}
  customElements.define('cart-drawer', CartDrawer);

  class CartItems extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.debouncedChange = debounce((input) => this.onQuantityChange(input), 350);

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-line-quantity]')) this.debouncedChange(e.target);
        if (e.target.matches('[data-cart-note]')) Cart.updateNote(e.target.value);
      });

      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          e.preventDefault();
          this.update(remove.dataset.key, 0, remove.closest('.cart-item'));
        }
      });
    }

    onQuantityChange(input) {
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      this.update(input.dataset.key, qty, input.closest('.cart-item'));
    }

    async update(key, qty, row) {
      if (row) row.classList.add('is-loading');
      try {
        await Cart.change(key, qty);
      } catch (err) {
        if (row) row.classList.remove('is-loading');
        showToast(err.message || theme.strings.error);
        Cart.refresh();
      }
    }
  }
  customElements.define('cart-items', CartItems);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.input = qs('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty]');
        if (!btn) return;
        e.preventDefault();
        const step = parseInt(this.input.step, 10) || 1;
        const min = parseInt(this.input.min, 10) || 0;
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const current = parseInt(this.input.value, 10) || 0;
        const next = btn.dataset.qty === 'plus' ? current + step : current - step;
        this.input.value = Math.min(max, Math.max(min, next));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Product form (AJAX add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.form = qs('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(e) {
      if (theme.cartType !== 'drawer' || !document.getElementById('CartDrawer')) return;
      e.preventDefault();
      const submitter = e.submitter && e.submitter.classList.contains('button') ? e.submitter : qs('[type="submit"]', this.form);
      const buttons = qsa(`[type="submit"][form="${this.form.id}"]`).concat(submitter ? [submitter] : []);
      if (submitter && submitter.getAttribute('aria-disabled') === 'true') return;
      const errorEl = qs('[data-form-error]', this);
      if (errorEl) errorEl.hidden = true;
      buttons.forEach((b) => b.classList.add('is-loading'));

      const formData = new FormData(this.form);
      formData.append('sections', Cart.sectionIds().join(','));
      formData.append('sections_url', window.location.pathname);

      try {
        const res = await fetch(`${theme.routes.cartAdd}`, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body: formData
        });
        const data = await res.json();
        if (data.status) {
          const msg = data.description || data.message || theme.strings.error;
          if (errorEl) {
            errorEl.textContent = msg;
            errorEl.hidden = false;
          } else {
            showToast(msg);
          }
          return;
        }
        Cart.renderSections(data.sections);
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
        const drawer = document.getElementById('CartDrawer');
        if (drawer && !this.closest('cart-drawer')) drawer.open(submitter);
      } catch (err) {
        showToast(theme.strings.error);
      } finally {
        buttons.forEach((b) => b.classList.remove('is-loading'));
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ==========================================================================
     Slider / carousel (scroll-snap based)
     ========================================================================== */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.track = qs('[data-track]', this);
      if (!this.track) return;
      this.prevBtn = qs('[data-prev]', this);
      this.nextBtn = qs('[data-next]', this);
      this.progress = qs('[data-progress]', this);
      this.counter = qs('[data-counter-current]', this);
      this.thumbs = qsa('[data-thumb]', this.closest('[data-gallery-root]') || this);
      this.dots = qsa('[data-dot]', this);

      this.prevBtn && this.prevBtn.addEventListener('click', () => this.step(-1));
      this.nextBtn && this.nextBtn.addEventListener('click', () => this.step(1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(parseInt(t.dataset.thumb, 10))));
      this.dots.forEach((d) => d.addEventListener('click', () => this.goTo(parseInt(d.dataset.dot, 10))));

      this.onScroll = () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this._ro = new ResizeObserver(() => this.update());
      this._ro.observe(this.track);
      this.update();

      if (this.dataset.autoplay) this.startAutoplay(parseInt(this.dataset.autoplay, 10) * 1000);
    }

    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
      clearInterval(this._autoplay);
    }

    get slides() {
      return qsa('[data-slide]', this.track).filter((s) => s.offsetParent !== null);
    }

    get isScrollable() {
      return this.track.scrollWidth > this.track.clientWidth + 2;
    }

    currentIndex() {
      const slides = this.slides;
      const left = this.track.scrollLeft;
      const base = slides[0] ? slides[0].offsetLeft : 0;
      let idx = 0;
      let min = Infinity;
      slides.forEach((s, i) => {
        const d = Math.abs(s.offsetLeft - base - left);
        if (d < min) {
          min = d;
          idx = i;
        }
      });
      return idx;
    }

    goTo(index, smooth = true) {
      const slides = this.slides;
      const slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
      if (!slide) return;
      if (!this.isScrollable) {
        slide.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'nearest' });
        return;
      }
      const base = slides[0].offsetLeft;
      this.track.scrollTo({ left: slide.offsetLeft - base, behavior: smooth ? 'smooth' : 'auto' });
    }

    step(dir) {
      const perPage = Math.max(1, Math.round(this.track.clientWidth / (this.slides[0]?.offsetWidth || 1)));
      const slideBy = this.dataset.slideBy === 'page' ? perPage : 1;
      this.goTo(this.currentIndex() + dir * slideBy);
    }

    update() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      if (this.prevBtn) this.prevBtn.disabled = t.scrollLeft <= 2;
      if (this.nextBtn) this.nextBtn.disabled = t.scrollLeft >= max - 2;
      const controls = qs('[data-controls]', this) || qs('[data-controls]', this.closest('.shopify-section') || this);
      if (controls) controls.hidden = !this.isScrollable;
      if (this.progress) {
        const ratio = t.clientWidth / t.scrollWidth;
        const pos = max > 0 ? t.scrollLeft / max : 0;
        this.progress.style.width = `${ratio * 100}%`;
        this.progress.style.transform = `translateX(${pos * ((1 - ratio) / ratio) * 100}%)`;
        this.progress.parentElement.hidden = !this.isScrollable;
      }
      const idx = this.currentIndex();
      if (this.counter) this.counter.textContent = idx + 1;
      this.thumbs.forEach((th, i) => th.setAttribute('aria-current', i === idx ? 'true' : 'false'));
      this.dots.forEach((d, i) => d.setAttribute('aria-current', i === idx ? 'true' : 'false'));
    }

    startAutoplay(ms) {
      clearInterval(this._autoplay);
      this._autoplay = setInterval(() => {
        if (this.matches(':hover')) return;
        const idx = this.currentIndex();
        this.goTo(idx >= this.slides.length - 1 ? 0 : idx + 1);
      }, ms);
    }

    /* Bring a media item into view (used for variant changes) */
    showMedia(mediaId) {
      const slide = qs(`[data-media-id="${mediaId}"]`, this.track);
      if (!slide) return;
      if (!this.isScrollable && window.matchMedia('(min-width: 990px)').matches) {
        this.track.prepend(slide);
        return;
      }
      this.goTo(this.slides.indexOf(slide));
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------- Tabs ---------- */
  class TabList extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.tabs = qsa('[role="tab"]', this);
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (e) => {
          const i = this.tabs.indexOf(tab);
          if (e.key === 'ArrowRight') this.select(this.tabs[(i + 1) % this.tabs.length], true);
          if (e.key === 'ArrowLeft') this.select(this.tabs[(i - 1 + this.tabs.length) % this.tabs.length], true);
        });
      });
    }

    select(tab, focus) {
      this.tabs.forEach((t) => {
        const selected = t === tab;
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        t.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) {
          panel.classList.toggle('is-active', selected);
          panel.hidden = !selected;
          if (selected) qsa('slider-component', panel).forEach((s) => s.update && s.update());
        }
      });
      if (focus) tab.focus();
      const link = qs('[data-tab-link]', this.closest('.shopify-section') || document);
      if (link && tab.dataset.url) link.href = tab.dataset.url;
    }
  }
  customElements.define('tab-list', TabList);

  /* ---------- Announcement bar ---------- */
  class AnnouncementSlider extends HTMLElement {
    connectedCallback() {
      this.items = qsa('.announcement__item', this);
      if (this.items.length < 2) return;
      this.index = 0;
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
    }
    disconnectedCallback() {
      this.stop();
    }
    start() {
      this.stop();
      this.timer = setInterval(() => this.show(this.index + 1), this.speed);
    }
    stop() {
      clearInterval(this.timer);
    }
    show(i) {
      this.index = (i + this.items.length) % this.items.length;
      this.items.forEach((el, n) => {
        el.classList.toggle('is-active', n === this.index);
        el.setAttribute('aria-hidden', n === this.index ? 'false' : 'true');
      });
    }
  }
  customElements.define('announcement-slider', AnnouncementSlider);

  /* ==========================================================================
     Header
     ========================================================================== */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.group = this.closest('[data-header-group]') || document.querySelector('[data-header-group]');
      if (!this.group) return;
      this.group.classList.toggle('is-sticky', this.dataset.sticky === 'true');
      this.group.classList.toggle('is-transparent', this.dataset.transparent === 'true');
      if (this.dataset.transparentText) this.group.style.setProperty('--transparent-text', this.dataset.transparentText);

      this._ro = new ResizeObserver(() => this.measure());
      this._ro.observe(this);
      this.measure();

      this.onScroll = () => {
        this.group.classList.toggle('is-scrolled', window.scrollY > 10);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      this.menus = qsa('details[data-nav-dropdown]', this);
      const canHover = window.matchMedia('(hover: hover)').matches;
      this.menus.forEach((d) => {
        if (canHover) {
          d.addEventListener('mouseenter', () => this.openMenu(d));
          d.addEventListener('mouseleave', () => this.closeMenu(d));
        }
        d.addEventListener('toggle', () => {
          if (d.open) this.menus.forEach((o) => o !== d && (o.open = false));
          this.syncHover();
        });
      });

      this.addEventListener('mouseenter', () => this.group.classList.add('is-hovered'));
      this.addEventListener('mouseleave', () => this.syncHover());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const open = this.menus.find((m) => m.open);
          if (open) {
            open.open = false;
            qs('summary', open).focus();
          }
        }
      });
      this._outside = (e) => {
        if (!this.contains(e.target)) this.menus.forEach((m) => (m.open = false));
      };
      document.addEventListener('click', this._outside);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('click', this._outside);
      if (this._ro) this._ro.disconnect();
      if (this.group) this.group.classList.remove('is-sticky', 'is-transparent', 'is-hovered');
    }

    measure() {
      const h = this.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${h}px`);
      document.documentElement.style.setProperty('--header-group-height', `${h}px`);
      document.documentElement.style.setProperty('--header-sticky-height', this.dataset.sticky === 'true' ? `${h}px` : '0px');
      document.documentElement.style.setProperty('--header-offset', this.dataset.transparent === 'true' ? `${h}px` : '0px');
    }

    openMenu(d) {
      clearTimeout(d._t);
      d.open = true;
    }

    closeMenu(d) {
      d._t = setTimeout(() => {
        d.open = false;
      }, 120);
    }

    syncHover() {
      const anyOpen = this.menus.some((m) => m.open);
      this.group.classList.toggle('is-hovered', anyOpen || this.matches(':hover'));
    }
  }
  customElements.define('site-header', SiteHeader);

  /* ==========================================================================
     Predictive search
     ========================================================================== */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      this.defaults = qs('[data-predictive-defaults]', this);
      if (!this.input || !this.results || this.dataset.enabled !== 'true') return;
      this.cache = {};
      this.input.addEventListener(
        'input',
        debounce(() => this.search(this.input.value.trim()), 250)
      );
    }

    async search(term) {
      if (!term) {
        this.results.innerHTML = '';
        this.results.hidden = true;
        if (this.defaults) this.defaults.hidden = false;
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        let html = this.cache[term];
        if (!html) {
          const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,collection,page,article&resources[limit]=8&resources[limit_scope]=each`;
          const res = await fetch(url, { signal: this.controller.signal });
          const text = await res.text();
          const doc = parseHTML(text);
          const content = qs('[data-predictive-content]', doc);
          html = content ? content.innerHTML : '';
          this.cache[term] = html;
        }
        this.results.innerHTML = html;
        this.results.hidden = false;
        if (this.defaults) this.defaults.hidden = true;
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ==========================================================================
     Variant picker
     ========================================================================== */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.sectionId = this.dataset.section;
      const json = qs('script[type="application/json"]', this);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability(this.selectedOptions());
    }

    selectedOptions() {
      return qsa('[data-option-position]', this).map((group) => {
        const select = qs('select', group);
        if (select) return select.value;
        const checked = qs('input:checked', group);
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const selected = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      this.updateLegend(selected);
      this.updateAvailability(selected);
      this.applyVariant(variant);
    }

    updateLegend(selected) {
      qsa('[data-option-position]', this).forEach((group, i) => {
        const name = qs('[data-option-value-name]', group);
        if (name) name.textContent = selected[i] || '';
      });
    }

    updateAvailability(selected) {
      qsa('[data-option-position]', this).forEach((group, pos) => {
        qsa('input, option', group).forEach((input) => {
          const value = input.value;
          const available = this.variants.some(
            (v) => v.available && v.options[pos] === value && v.options.every((o, i) => i === pos || o === selected[i] || selected[i] === null)
          );
          input.classList.toggle('is-unavailable', !available);
          if (input.tagName === 'OPTION') {
            input.textContent = input.dataset.label + (available ? '' : ` — ${theme.strings.unavailable}`);
          }
        });
      });
    }

    applyVariant(variant) {
      const sid = this.sectionId;
      qsa(`[data-variant-id-input="${sid}"]`).forEach((input) => {
        input.value = variant ? variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      qsa(`[data-atc="${sid}"]`).forEach((btn) => {
        const label = qs('[data-atc-label]', btn);
        const price = qs('[data-atc-price]', btn);
        const ok = variant && variant.available;
        btn.disabled = !ok;
        btn.setAttribute('aria-disabled', ok ? 'false' : 'true');
        if (label) label.textContent = !variant ? theme.strings.unavailable : ok ? btn.dataset.labelAdd || theme.strings.addToCart : theme.strings.soldOut;
        if (price) price.textContent = variant ? formatMoney(variant.price) : '';
      });

      if (!variant) return;

      qsa(`[data-price="${sid}"]`).forEach((el) => {
        el.innerHTML = VariantPicker.priceHTML(variant);
      });
      qsa(`[data-sku="${sid}"]`).forEach((el) => {
        el.textContent = variant.sku || '';
        el.closest('[data-sku-wrapper]') && (el.closest('[data-sku-wrapper]').hidden = !variant.sku);
      });

      if (variant.featured_media) {
        qsa(`[data-gallery="${sid}"]`).forEach((g) => g.showMedia && g.showMedia(variant.featured_media.id));
        qsa(`[data-sticky-image="${sid}"]`).forEach((img) => {
          if (variant.featured_media.preview_image) {
            img.src = variant.featured_media.preview_image.src + (variant.featured_media.preview_image.src.includes('?') ? '&' : '?') + 'width=120';
            img.removeAttribute('srcset');
          }
        });
      }

      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      document.dispatchEvent(new CustomEvent('variant:change', { detail: { sectionId: sid, variant } }));
    }

    static priceHTML(v) {
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      let html = `<span class="price${onSale ? ' price--on-sale' : ''}">`;
      if (onSale) {
        html += `<span class="visually-hidden">${theme.strings.salePrice || ''}</span><span class="price__current">${formatMoney(v.price)}</span><s class="price__compare">${formatMoney(v.compare_at_price)}</s>`;
      } else {
        html += `<span class="price__current">${formatMoney(v.price)}</span>`;
      }
      if (v.unit_price_measurement && v.unit_price) {
        const m = v.unit_price_measurement;
        html += `<span class="price__unit">${formatMoney(v.unit_price)}/${m.reference_value !== 1 ? m.reference_value : ''}${m.reference_unit}</span>`;
      }
      return html + '</span>';
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Product card swatches ---------- */
  document.addEventListener('click', (e) => {
    const swatch = e.target.closest('[data-card-swatch]');
    if (!swatch) return;
    e.preventDefault();
    const card = swatch.closest('.card');
    if (!card) return;
    qsa('[data-card-swatch]', card).forEach((s) => s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false'));
    const d = swatch.dataset;
    const idInput = qs('input[name="id"]', card);
    if (idInput) idInput.value = d.variantId;
    const img = qs('.card__img--primary', card);
    if (img && d.image) {
      img.src = d.image;
      if (d.srcset) img.srcset = d.srcset;
    }
    const price = qs('[data-card-price]', card);
    if (price && d.priceHtml) price.innerHTML = d.priceHtml;
    const btn = qs('[data-card-atc]', card);
    if (btn) {
      const available = d.available === 'true';
      btn.disabled = !available;
      btn.setAttribute('aria-disabled', available ? 'false' : 'true');
      const label = qs('[data-atc-label]', btn);
      if (label) label.textContent = available ? btn.dataset.labelAdd : theme.strings.soldOut;
      const p = qs('[data-atc-price]', btn);
      if (p && d.price) p.textContent = d.price;
    }
    qsa('a[data-card-link]', card).forEach((a) => {
      const url = new URL(a.href, window.location.origin);
      url.searchParams.set('variant', d.variantId);
      a.href = url.pathname + url.search;
    });
  });

  /* ==========================================================================
     Sticky add to cart
     ========================================================================== */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.dataset.target);
      if (!target) return;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', past);
          this.setAttribute('aria-hidden', past ? 'false' : 'true');
        },
        { threshold: 0 }
      );
      this.observer.observe(target);
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ==========================================================================
     Product recommendations
     ========================================================================== */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this._loaded) return;
      const load = () => {
        this._loaded = true;
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((text) => {
            const doc = parseHTML(text);
            const fresh = qs('product-recommendations', doc);
            if (fresh && fresh.innerHTML.trim().length) {
              this.innerHTML = fresh.innerHTML;
            } else {
              const section = this.closest('.shopify-section');
              if (section && !window.Shopify?.designMode) section.hidden = true;
            }
          })
          .catch(() => {});
      };
      const io = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      io.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ==========================================================================
     Collection filters & sorting (Section Rendering API)
     ========================================================================== */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.sectionId = this.dataset.sectionId;
      this.form = document.getElementById(this.dataset.form);
      this.debounced = debounce(() => this.submit(), 450);

      document.addEventListener('change', (this._onChange = (e) => {
        const el = e.target;
        if (!el.form || el.form !== this.form) return;
        if (el.name === 'sort_by') qsa(`select[name="sort_by"][form="${this.form.id}"], select[name="sort_by"]`, document).forEach((s) => (s.value = el.value));
        this.debounced();
      }));

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submit();
      });

      document.addEventListener('click', (this._onClick = (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (!link) return;
        e.preventDefault();
        const url = new URL(link.href, window.location.origin);
        this.render(url.searchParams.toString());
      }));

      window.addEventListener('popstate', (this._onPop = () => this.render(window.location.search.slice(1), false)));
    }

    disconnectedCallback() {
      document.removeEventListener('change', this._onChange);
      document.removeEventListener('click', this._onClick);
      window.removeEventListener('popstate', this._onPop);
    }

    params() {
      const fd = new FormData(this.form);
      const params = new URLSearchParams();
      const seen = new Set();
      for (const [k, v] of fd.entries()) {
        if (v === '') continue;
        const key = `${k}=${v}`;
        if (seen.has(key)) continue;
        seen.add(key);
        params.append(k, v);
      }
      qsa(`[form="${this.form.id}"]`).forEach((el) => {
        if (el.name && el.value && !params.has(el.name)) params.append(el.name, el.value);
      });
      return params.toString();
    }

    submit() {
      this.render(this.params());
    }

    async render(search, push = true) {
      const results = document.getElementById(`CollectionResults-${this.sectionId}`);
      if (results) results.classList.add('is-loading');
      const url = `${window.location.pathname}?${search}${search ? '&' : ''}section_id=${this.sectionId}`;
      try {
        const res = await fetch(url);
        const doc = parseHTML(await res.text());
        ['CollectionResults', 'ActiveFacets', 'FacetsBody', 'ProductCount'].forEach((prefix) => {
          const id = `${prefix}-${this.sectionId}`;
          const current = document.getElementById(id);
          const fresh = doc.getElementById(id);
          if (!current || !fresh) return;
          if (prefix === 'FacetsBody') {
            const openIds = qsa('details[open]', current).map((d) => d.id);
            current.innerHTML = fresh.innerHTML;
            openIds.forEach((oid) => {
              const d = document.getElementById(oid);
              if (d) d.open = true;
            });
          } else {
            current.innerHTML = fresh.innerHTML;
          }
        });
        if (push) window.history.pushState({}, '', `${window.location.pathname}${search ? '?' + search : ''}`);
      } finally {
        const r = document.getElementById(`CollectionResults-${this.sectionId}`);
        if (r) r.classList.remove('is-loading');
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ==========================================================================
     Misc
     ========================================================================== */
  // Localization selectors auto-submit
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-localization-select]')) e.target.form.submit();
  });

  // Share button
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    e.preventDefault();
    const data = { title: document.title, url: btn.dataset.url || window.location.href };
    if (navigator.share) {
      try {
        await navigator.share(data);
      } catch (err) {
        /* dismissed */
      }
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(data.url);
      showToast(theme.strings.linkCopied);
    }
  });

  // Reveal on scroll
  function initReveal(root = document) {
    const els = qsa('.reveal:not(.is-visible)', root);
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px' }
    );
    els.forEach((el) => io.observe(el));
  }
  document.addEventListener('DOMContentLoaded', () => initReveal());

  // Pause hero videos when out of view (saves battery)
  document.addEventListener('DOMContentLoaded', () => {
    const vids = qsa('video[data-autoplay-video]');
    if (!vids.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => (en.isIntersecting ? en.target.play().catch(() => {}) : en.target.pause()));
    });
    vids.forEach((v) => io.observe(v));
  });


  /* ==========================================================================
     Soft-edge interactions: directional button wipe, swipe cursor, drag scroll
     ========================================================================== */
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  // Button hover: the fill wipes in from the edge the pointer enters and out through the edge it leaves.
  (function initButtonWipe() {
    const nearestEdge = (el, e) => {
      const r = el.getBoundingClientRect();
      const d = { left: e.clientX - r.left, right: r.right - e.clientX, top: e.clientY - r.top, bottom: r.bottom - e.clientY };
      return Object.keys(d).sort((a, b) => d[a] - d[b])[0];
    };
    const isInert = (b) => b.disabled || b.getAttribute('aria-disabled') === 'true';

    document.addEventListener('mouseover', (e) => {
      if (!canHover.matches) return;
      const btn = e.target.closest && e.target.closest('.button');
      if (!btn || btn.contains(e.relatedTarget) || isInert(btn)) return;
      btn.classList.add('is-resetting');
      btn.dataset.wipe = nearestEdge(btn, e);
      void btn.offsetWidth;
      btn.classList.remove('is-resetting');
      btn.classList.add('is-hovered');
    });

    document.addEventListener('mouseout', (e) => {
      const btn = e.target.closest && e.target.closest('.button');
      if (!btn || btn.contains(e.relatedTarget)) return;
      btn.dataset.wipe = nearestEdge(btn, e);
      btn.classList.remove('is-hovered');
    });
  })();

  // "swipe" cursor that follows the mouse over carousels, plus mouse drag-to-scroll
  (function initSwipeCursor() {
    let cursor = null;
    let tx = 0;
    let ty = 0;
    let x = 0;
    let y = 0;
    let raf = null;
    let active = false;

    const ensure = () => {
      if (cursor) return cursor;
      cursor = document.createElement('div');
      cursor.className = 'swipe-cursor';
      cursor.setAttribute('aria-hidden', 'true');
      cursor.textContent = (theme.strings && theme.strings.swipe) || 'swipe';
      document.body.appendChild(cursor);
      return cursor;
    };

    const loop = () => {
      x += (tx - x) * 0.22;
      y += (ty - y) * 0.22;
      cursor.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
      raf = active || Math.abs(tx - x) > 0.5 || Math.abs(ty - y) > 0.5 ? requestAnimationFrame(loop) : null;
    };

    const setActive = (on) => {
      if (on === active) return;
      active = on;
      ensure().classList.toggle('is-visible', on);
      if (on && !raf) raf = requestAnimationFrame(loop);
    };

    document.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || !canHover.matches) return;
      const zone = e.target.closest && e.target.closest('[data-swipe-cursor]');
      const overControl = e.target.closest && e.target.closest('.carousel__dots, .button, button, input, select, textarea');
      const show = Boolean(zone) && !overControl;
      if (show) {
        ensure();
        if (!active) {
          x = tx = e.clientX;
          y = ty = e.clientY;
        } else {
          tx = e.clientX;
          ty = e.clientY;
        }
      }
      if (zone) zone.classList.toggle('has-swipe-cursor', show);
      setActive(show);
    });

    document.addEventListener('pointerleave', () => setActive(false));
    document.addEventListener('mouseleave', () => setActive(false));
    window.addEventListener('blur', () => setActive(false));

    // Drag to scroll with a mouse (touch and trackpads already scroll natively)
    let drag = null;
    let suppressClick = false;

    document.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      const track = e.target.closest && e.target.closest('[data-swipe-cursor] [data-track]');
      if (!track || track.scrollWidth <= track.clientWidth + 2) return;
      drag = { track, startX: e.clientX, startLeft: track.scrollLeft, moved: false };
    });

    document.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      if (!drag.moved && Math.abs(dx) > 5) {
        drag.moved = true;
        drag.track.classList.add('is-dragging');
      }
      if (drag.moved) drag.track.scrollLeft = drag.startLeft - dx;
    });

    const endDrag = () => {
      if (!drag) return;
      const { track, moved } = drag;
      drag = null;
      if (!moved) return;
      suppressClick = true;
      setTimeout(() => (suppressClick = false), 0);
      track.classList.remove('is-dragging');
      const slider = track.closest('slider-component');
      if (slider && typeof slider.goTo === 'function') slider.goTo(slider.currentIndex());
    };
    document.addEventListener('pointerup', endDrag);
    document.addEventListener('pointercancel', endDrag);

    document.addEventListener(
      'click',
      (e) => {
        if (!suppressClick) return;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );
    document.addEventListener('dragstart', (e) => {
      if (drag && e.target.closest && e.target.closest('[data-swipe-cursor]')) e.preventDefault();
    });
  })();

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

  document.addEventListener('shopify:block:select', (e) => {
    const slider = e.target.closest('announcement-slider');
    if (slider && slider.items) {
      slider.stop();
      slider.show(slider.items.indexOf(e.target));
    }
    const dropdown = e.target.closest('details[data-nav-dropdown]');
    if (dropdown) dropdown.open = true;
    const tabPanel = e.target.closest('[role="tabpanel"]');
    if (tabPanel) {
      const tab = qs(`[aria-controls="${tabPanel.id}"]`);
      const list = tab && tab.closest('tab-list');
      if (list) list.select(tab);
    }
    const slide = e.target.closest('[data-slide]');
    const sc = slide && slide.closest('slider-component');
    if (sc) sc.goTo(sc.slides.indexOf(slide), false);
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    const slider = e.target.closest('announcement-slider');
    if (slider && slider.start) slider.start();
  });
})();
